import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { useState } from "react";
import { BootstrapDialog } from "../notification-modal/styles";
import { colors, fonts } from "../../../utility/types/constant";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SearchIcon from "@mui/icons-material/Search";
import {
  EmptySearchUserSendAddRequestPayload,
  ISearchUserSendAddRequestPayload,
  IUser,
} from "../../../utility/types/userType";
import { Formik, Form } from "formik";
import { searchUserToSendAddRequestVal } from "../../../utility/validation/searchUserToSendAddRequestValidation";
import { fetchSearchUserToSendAddRequest } from "../../../redux/actions/userActions";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../redux/store/store";
import { selectUserIsLoading } from "../../../redux/selectors/userSelector";
import Scrollbar from "../../scrollbar/Scrollbar";
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";
import CircleTableHead from "../../circle-contribution-table/CircleTableHead";
import SearchUserResultTable from "./SearchUserResultTable";
import NotificationModal from "../notification-modal/NotificationModal";
import { useParams } from "react-router-dom";

interface SearchUserToSendAddRequestModalProps {
  open: boolean;
  handleClose: () => void;
  sx?: any;
}

const SearchUserToSendAddRequestModal = (
  props: SearchUserToSendAddRequestModalProps
) => {
  const dispatch = useDispatch<AppDispatch>();

  const [userResult, setUserResult] = useState<IUser | null>();

  const [showNotificationModal, setShowNotificationModal] = useState(false);

  const [notificationMessage, setNotificationMessage] = useState("");

  const [error, setError] = useState("");

  const { circleId } = useParams<{ circleId: string }>();

  const { open, handleClose, sx } = props;

  const isLoading = useSelector(selectUserIsLoading);

  const initialValues: ISearchUserSendAddRequestPayload =
    EmptySearchUserSendAddRequestPayload;

  const normalizePhoneNumber = (phoneNumber: string) => {
    return phoneNumber.replace(/\D/g, "");
  };

  const handleSearchSubmit = async (
    values: ISearchUserSendAddRequestPayload,
    { resetForm }: any
  ) => {
    const normalizedPhone = normalizePhoneNumber(values.phone?.number || "");

    const searchValues = {
      ...values,
      phone: { ...values.phone, number: normalizedPhone },
    };

    try {
      const response = await dispatch(
        fetchSearchUserToSendAddRequest(searchValues)
      );

      if (response.payload) {
        setUserResult(response.payload);
        // Reset the form values to their initial state
        setError("");
        resetForm();
      } else {
        setUserResult(null);
        setError("User not found");
        resetForm();
      }
    } catch (error) {
      setUserResult(null);
      setError("An error occurred");
      resetForm();
    }
  };

  const handleOpenNotificationModal = (message: string) => {
    setNotificationMessage(message);

    setShowNotificationModal(true);
  };

  const handleCloseNotificationModal = () => {
    setShowNotificationModal(false);
  };

  return (
    <React.Fragment>
      <BootstrapDialog
        open={open}
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        sx={{ ...sx, width: "100%" }}
        maxWidth="lg"
        fullWidth
      >
        {/* ── Header band ──────────────────────────────────────────── */}
        <Box
          sx={{
            bgcolor: colors.primary,
            px: { xs: 2.5, sm: 4 },
            py: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: alpha(colors.mainSecondary, 0.15),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <PersonAddIcon sx={{ color: colors.mainSecondary, fontSize: 22 }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontFamily: fonts.montserrat,
                  fontWeight: 800,
                  fontSize: { xs: "1rem", sm: "1.15rem" },
                  color: "#fff",
                  lineHeight: 1.2,
                }}
              >
                Search & Add Member
              </Typography>
              <Typography
                sx={{
                  fontFamily: fonts.montserrat,
                  fontSize: "0.72rem",
                  color: alpha("#fff", 0.6),
                  mt: 0.3,
                }}
              >
                Search by name, email or phone to send an add request
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ color: alpha("#fff", 0.7) }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ pt: 3, pb: 2, px: { xs: 2.5, sm: 4 } }}>
          {/* ── Search form ─────────────────────────────────────────── */}
          <Formik
            initialValues={initialValues}
            validationSchema={searchUserToSendAddRequestVal}
            onSubmit={handleSearchSubmit}
          >
            {({ errors, touched, values, handleChange }) => (
              <Form>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      id="firstName"
                      label="First Name"
                      variant="outlined"
                      size="small"
                      name="firstName"
                      required
                      autoFocus
                      fullWidth
                      value={values.firstName}
                      onChange={handleChange}
                      error={!!(errors?.firstName && touched?.firstName)}
                      helperText={
                        errors.firstName && touched.firstName && errors.firstName
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      id="lastName"
                      required
                      label="Last Name"
                      name="lastName"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={values.lastName}
                      onChange={handleChange}
                      error={!!(errors?.lastName && touched?.lastName)}
                      helperText={
                        errors.lastName && touched.lastName && errors.lastName
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      id="email"
                      label="Email"
                      required
                      name="email"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={values.email}
                      onChange={handleChange}
                      error={!!(errors?.email && touched?.email)}
                      helperText={errors.email && touched.email && errors.email}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      id="phone"
                      label="Phone Number"
                      variant="outlined"
                      size="small"
                      name="phone.number"
                      fullWidth
                      value={values.phone?.number || ""}
                      onChange={handleChange}
                      error={!!errors.phone && touched?.phone}
                      helperText={errors.phone && touched?.phone && errors.phone}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={1}>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      startIcon={
                        isLoading ? (
                          <CircularProgress size={14} sx={{ color: colors.primary }} />
                        ) : (
                          <SearchIcon />
                        )
                      }
                      disabled={isLoading}
                      sx={{
                        bgcolor: colors.mainSecondary,
                        color: colors.primary,
                        fontFamily: fonts.montserrat,
                        fontWeight: 800,
                        textTransform: "none",
                        borderRadius: 2,
                        boxShadow: `0 4px 14px ${alpha(colors.mainSecondary, 0.4)}`,
                        "&:hover": { bgcolor: colors.mainSecondary },
                        "&.Mui-disabled": { bgcolor: alpha(colors.mainSecondary, 0.5) },
                      }}
                    >
                      Search
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>

          <Divider sx={{ mt: 3, mb: 2 }} />

          {/* ── Results ─────────────────────────────────────────────── */}
          <Scrollbar sx={{ minHeight: 120 }}>
            {isLoading ? (
              <LoadingSpinner loading={isLoading} />
            ) : (
              <TableContainer sx={{ overflow: "auto" }}>
                {userResult && (
                  <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha(colors.primary, 0.04) }}>
                        {["Member", "Full Name", "Email", "Phone", "Action"].map((h) => (
                          <TableCell
                            key={h}
                            sx={{
                              fontFamily: fonts.montserrat,
                              fontWeight: 700,
                              fontSize: "0.72rem",
                              color: colors.darkGrey,
                              textTransform: "uppercase",
                              letterSpacing: "0.06em",
                              py: 1.5,
                            }}
                          >
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      <SearchUserResultTable
                        key={userResult?._id}
                        profileImage={userResult?.profileImage ?? ""}
                        requestId={userResult?._id ?? ""}
                        fullName={`${userResult?.firstName} ${userResult?.lastName}`}
                        email={userResult?.email ?? ""}
                        phone={
                          userResult?.phone?.number
                            ? userResult?.phone?.number ?? ""
                            : "N/A"
                        }
                        handleOpenNotificationModal={
                          handleOpenNotificationModal
                        }
                        hasPendingAddRequest={
                          // Disable if a pending add request exists OR if the membership count is ≥ 2

                          userResult.pendingAddRequests?.some(
                            (request) =>
                              request.union?._id === circleId &&
                              ["Pending", "Rejected", "Approved"].includes(
                                request.status
                              )
                          ) || false
                        }
                      />
                    </TableBody>
                  </Table>
                )}

                {error && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      py: 4,
                      gap: 1,
                    }}
                  >
                    <PersonAddIcon
                      sx={{ fontSize: 40, color: alpha(colors.primary, 0.18) }}
                    />
                    <Typography
                      sx={{
                        fontFamily: fonts.montserrat,
                        fontWeight: 600,
                        fontSize: "0.88rem",
                        color: alpha(colors.primary, 0.45),
                      }}
                    >
                      No user found matching your search criteria.
                    </Typography>
                  </Box>
                )}
              </TableContainer>
            )}
          </Scrollbar>

          {showNotificationModal && (
            <NotificationModal
              open={showNotificationModal}
              handleClose={handleCloseNotificationModal}
              title="Send Add Request Successful"
            >
              <Typography
                sx={{
                  display: "flex",
                  textAlign: "center",
                  justifyContent: "center",
                  fontWeight: 500,
                }}
              >
                {notificationMessage}
              </Typography>
            </NotificationModal>
          )}
        </DialogContent>
      </BootstrapDialog>
    </React.Fragment>
  );
};

export default SearchUserToSendAddRequestModal;

SearchUserToSendAddRequestModal.propType = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  sx: PropTypes.any,
};
